import { Box, Divider, IconButton, Typography } from '@mui/material'
import { IconInfo } from 'assets/icons'
import figmaLogoSrc from 'assets/images/figma-logo.svg'
import gitHubLogoSrc from 'assets/images/github-logo.svg'
import gitLabLogoSrc from 'assets/images/gitlab-logo.svg'
import { AdvancedTooltip, HelperInfo } from 'elements/feedback'
import { useTranslation } from 'react-i18next'

const integrationVariants = {
  GITHUB: {
    logoSrc: gitHubLogoSrc,
    title: 'GitHub',
    subtitle: 'projects.settings.integrations.github_description',
  },
  FIGMA: {
    logoSrc: figmaLogoSrc,
    title: 'Figma',
    subtitle: 'projects.settings.integrations.figma_description',
  },
  GITLAB: {
    logoSrc: gitLabLogoSrc,
    title: 'Gitlab',
    subtitle: 'projects.settings.integrations.github_description',
  },
}

type IntegrationsBlockProps = {
  variant: 'GITHUB' | 'FIGMA' | 'GITLAB'
  connectButton?: React.ReactNode
  connectedIntegrations?: React.ReactNode
}

export const IntegrationsBlock = ({
  variant,
  connectButton,
  connectedIntegrations,
}: IntegrationsBlockProps) => {
  const { t } = useTranslation()

  const { logoSrc, title, subtitle } = integrationVariants[variant]

  return (
    <Box
      display="flex"
      flexDirection="column"
      bgcolor="common.white"
      border="1px solid"
      borderColor="primary.light"
      borderRadius="12px"
      p={3}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" flex={2.8}>
          <Box component="img" src={logoSrc} mr={2} width={52} height={52} />

          <Box display="flex" flexDirection="column">
            <Box display="flex">
              <Typography variant="caption1" fontWeight={600} mb={0.25}>
                {title}
              </Typography>

              {variant !== 'GITLAB' && (
                <AdvancedTooltip
                  placement="right"
                  title={
                    <HelperInfo
                      text={t(
                        `projects.settings.integrations.info_helper_tooltip_text_${variant}`,
                      )}
                      title={t(
                        `projects.settings.integrations.info_helper_tooltip_title_${variant}`,
                      )}
                    />
                  }
                >
                  <IconButton sx={{ ml: 0.25 }}>
                    <IconInfo sx={{ fontSize: '14px', mb: 1 }} />
                  </IconButton>
                </AdvancedTooltip>
              )}
            </Box>

            <Typography variant="caption2" color="text.secondary">
              {t(subtitle)}
            </Typography>
          </Box>
        </Box>

        {connectButton}
      </Box>

      {connectedIntegrations && (
        <>
          <Divider sx={{ mt: 2.5, mb: 1.5 }} />

          <Typography variant="caption2" fontWeight={600} mb={1.5}>
            {variant === 'GITHUB'
              ? t('projects.settings.integrations.connected_integrations')
              : t('projects.settings.integrations.connected_integration')}
          </Typography>

          {connectedIntegrations}
        </>
      )}
    </Box>
  )
}
